import { useEffect, useState, useRef } from 'react';
import SignaturePad from 'signature_pad';
import { SignatureProps } from './types/propTypes';
import styles from './Signature.module.scss';

const Signature = ({ callback, cleared, isTouched, className, signatureData }: SignatureProps) => {
  const signatureElement = useRef<HTMLCanvasElement>(null);
  const [signPad, setSignPad] = useState<SignaturePad>();
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => {
    if (signatureElement.current) {
      const canvas = signatureElement.current;
      setCanvasSize(canvas);

      // Initialize SignaturePad and restore saved signature if it exists
      const pad = new SignaturePad(canvas);
      setSignPad(pad);

      if (signatureData) {
        // Restore the previous signature from the data URL
        pad.fromDataURL(signatureData);
      }
    }
  }, [signatureElement.current, signatureData]);
  useEffect(() => {
    if (signPad) {
      const handleResize = () => {
        setCanvasSize(signatureElement.current!);
        signPad.clear(); // Clears the signature as resizing distorts the drawing
      };

      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [signPad]);

  useEffect(() => {
    handleClear();
  }, [cleared]);

  useEffect(() => {
    if (signPad) {
      const onStrokeStart = () => {
        isTouched();
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };

      const onStrokeEnd = () => {
        timeoutRef.current = window.setTimeout(() => {
          const fileData = signPad.toDataURL();
          callback(fileData as string);
        }, 1000); // Adjust timeout duration as needed
      };

      signPad.addEventListener('beginStroke', onStrokeStart);
      signPad.addEventListener('endStroke', onStrokeEnd);

      return () => {
        signPad.removeEventListener('beginStroke', onStrokeStart);
        signPad.removeEventListener('endStroke', onStrokeEnd);
      };
    }
  }, [signPad]);

  const setCanvasSize = (canvas: HTMLCanvasElement) => {
    // Set the canvas width and height to match the actual CSS size
    const { width, height } = canvas.getBoundingClientRect();
    canvas.width = width;
    canvas.height = height;
  };

  const handleClear = () => {
    signPad?.clear();
  };

  return (
    <div>
      <canvas
        className={`${styles['signature_canvas']} ${className ?? ''}`}
        ref={signatureElement}
        data-testid="canvas"
      ></canvas>
    </div>
  );
};

Signature.displayName = 'Signature';
export default Signature;
